import {
  useMutation,
  UseMutationResult,
  UseMutationOptions,
  useQueryClient,
} from "@tanstack/react-query"
import { guardAppUsage } from "@mimber-client/guards/AppGuard"
import { useServerContext } from "@mimber-client/contexts/ServerContext"
import { InvitationWithRelationsDto, ResponseDto } from "@mimber/schema"
import { invitationsKeys } from "../invitations.utils"
import { ResendInvitationParams } from "./useResendInvitation.types"

/**
 * Organization-specific hook for resending an expired invitation
 * Creates a new invitation with a new UUID and expiration date
 * Can only be used in mimber-organization app
 * The organization context is automatically handled by ServerContext
 */
export const useResendInvitation = (
  options?: UseMutationOptions<
    ResponseDto<InvitationWithRelationsDto>,
    Error,
    ResendInvitationParams
  >
): UseMutationResult<
  ResponseDto<InvitationWithRelationsDto>,
  Error,
  ResendInvitationParams
> => {
  guardAppUsage("mimber-organization", "useResendInvitation")

  const { server } = useServerContext()
  const queryClient = useQueryClient()

  return useMutation({
    ...options,
    mutationFn: async ({ uuid }: ResendInvitationParams) => {
      const response = await server.post<
        ResponseDto<InvitationWithRelationsDto>
      >(`invitations/${uuid}/resend`)
      return response.data
    },
    onSuccess: (data, variables, onMutateResult, context) => {
      // Invalidate the specific invitation query
      queryClient.invalidateQueries({
        queryKey: invitationsKeys.findOne(variables.uuid),
      })

      // Invalidate all invitation lists to show the updated invitation
      queryClient.invalidateQueries({ queryKey: invitationsKeys.findAll() })

      // Call user's onSuccess if provided
      options?.onSuccess?.(data, variables, onMutateResult, context)
    },
  })
}
